import { useQuery, useQueryClient } from '@tanstack/react-query';
import Datev from '@/components/Datev/Datev';
import { useAppSelector } from '../../hooks';
import {
  activeCompanySelector,
  queryKeySelector,
} from '@/core/store/user/userSlice';
import { getDataUnits, getDatev, getDatevLedgerAggregated } from '@/core/api';
import { Loader } from '@/components/Loader/Loader';
import PageLayout from '@/wrappers/PageLayout';
import FeedbackWidget from '@/components/FeedbackWidget/FeedbackWidget';
import COAImage from '@/images/coa.svg?react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AuthedRoutesEnum } from '@/core/enums';
import { useFlags } from 'launchdarkly-react-client-sdk';
import NewDatev from '@/components/Datev/NewDatev';
import { useWSMessageHandler } from '@/core/ws/useWSMessageHandler';
import { isDatevGeneratedMessage } from '@/core/ws/ws-messages';
import { DatevFile } from '@/core/types';

export default function SellerDatev() {
  const { fibu121DatevCp } = useFlags();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const queryKey = useAppSelector(queryKeySelector);
  const activeCompany = useAppSelector(activeCompanySelector);
  const { data: files, isLoading } = useQuery({
    queryKey: ['SellerDatev', queryKey],
    queryFn: () => getDatev(activeCompany?.company_id),
  });

  const { data: aggregatedDatev, isLoading: isLoadingDatev } = useQuery({
    queryKey: ['SellerDatevAggregated', queryKey],
    queryFn: () => getDatevLedgerAggregated(activeCompany?.company_id),
  });

  const { data: dataUnits, isLoading: isLoadingDataUnits } = useQuery({
    queryKey: ['SellerDataUnits', queryKey],
    queryFn: () => getDataUnits(activeCompany?.company_id!),
    enabled: fibu121DatevCp,
  });

  const companyId = activeCompany?.company_id;
  useWSMessageHandler(
    (msg) => {
      if (
        fibu121DatevCp &&
        isDatevGeneratedMessage(msg) &&
        msg.data.company_id === companyId
      ) {
        getDatevLedgerAggregated(companyId)
          .then((res) => {
            queryClient.setQueryData(
              ['SellerDatev', queryKey],
              (prev: DatevFile[]) => {
                return prev.map((f) => {
                  if (f.id === msg.data.id) {
                    return {
                      ...f,
                      status: msg.data.status,
                      file: msg.data.file,
                    };
                  }
                  return f;
                });
              }
            );
            queryClient.setQueryData(['SellerDatevAggregated', queryKey], res);
          })
          .catch(console.error);
      }
    },
    // eslint-disable-next-line
    [companyId, queryKey, fibu121DatevCp]
  );

  return (
    <PageLayout
      className='flex-grow'
      rightColumn={
        <div style={{ maxWidth: 328 }} className='pt-4 lg:ml-auto lg:pt-0'>
          <FeedbackWidget testId='FeedbackWidget' />
          {fibu121DatevCp && (
            <div className='overflow-hidden rounded-lg bg-white'>
              <COAImage />
              <div className='p-4'>
                <div className='mb-4 text-h4 font-extrabold'>
                  {t('Set up the chart of accounts for the DATEV export')}
                </div>
                <Link
                  className='Button--outline'
                  to={AuthedRoutesEnum.ChartOfAccounts}
                >
                  {t('Set up now')}
                </Link>
              </div>
            </div>
          )}
        </div>
      }
    >
      <div className='mx-auto flex w-full max-w-[1328px] flex-grow flex-col'>
        {isLoading || isLoadingDatev || isLoadingDataUnits ? (
          <Loader />
        ) : fibu121DatevCp ? (
          <NewDatev
            files={files || []}
            companyId={activeCompany!.company_id}
            displaySinceISO={activeCompany?.ClientDatevDetails?.display_since}
            aggregatedDatev={aggregatedDatev || []}
            dataUnits={dataUnits || []}
          />
        ) : (
          <Datev
            files={files || []}
            companyId={activeCompany!.company_id}
            displaySinceISO={activeCompany?.ClientDatevDetails?.display_since}
            aggregatedDatev={aggregatedDatev || []}
          />
        )}
      </div>
    </PageLayout>
  );
}
